import React, { useEffect } from 'react';
import { t } from '@lingui/core/macro';

import { Box } from '@rover/kibble/core';
import { useKibbleForm } from '@rover/kibble/forms';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';

import useHomeAddress from '../../hooks/useHomeAddress';
import { AddressType } from '../../LocationInput.constants';
import { areAddressesDifferent } from '../../utils';

import AddressAlerts from './AddressAlerts';

type Props = {
  configureField: ReturnType<typeof useKibbleForm>['configureField'];
  getValues: ReturnType<typeof useKibbleForm>['getValues'];
  watch: ReturnType<typeof useKibbleForm>['watch'];
  setValue: ReturnType<typeof useKibbleForm>['setValue'];
  addressType: AddressType;
};

export default function FormFields({
  configureField,
  getValues,
  watch,
  setValue,
  addressType,
}: Props): JSX.Element {
  const { address: homeAddress } = useHomeAddress(true);
  const { user } = useCurrentUserShim();

  const formValues = watch();
  const currentAddress = {
    addressLine1: formValues.addressLine1,
    city: formValues.city,
    state: formValues.state,
    zip: formValues.zip,
    countryCode: formValues.countryCode,
  };

  const isAddressDifferentFromHome = areAddressesDifferent(currentAddress, homeAddress);
  const isProvider = user?.userMode === 'provider';
  const isHomeAddressChecked = getValues('isHomeAddress')?.includes('home');

  useEffect(() => {
    if (isAddressDifferentFromHome && isProvider) {
      setValue('isHomeAddress', []);
    }
  }, [isAddressDifferentFromHome, isProvider, setValue]);

  return (
    <Box mt="4x">
      <CheckboxField
        {...configureField('isHomeAddress')}
        options={[
          {
            primaryLabel: t`This is my home address`,
            value: 'home',
          },
        ]}
        primaryLabel=""
        helperMessage={
          isHomeAddressChecked
            ? t`This will be your default search address`
            : t`This address will be used for this request only`
        }
        disabled={isAddressDifferentFromHome && isProvider}
      />

      <AddressAlerts
        countryCodeField={configureField('countryCode')}
        countryCheckboxesField={configureField('countryCheckboxes')}
        isHomeAddressChecked={isHomeAddressChecked}
        isAddressDifferentFromHome={isAddressDifferentFromHome}
        isProvider={isProvider}
        addressType={addressType}
        homeCountryCode={homeAddress?.countryCode}
      />
    </Box>
  );
}
